/**
 * The cloud density field: tileable Perlin–Worley noise, generated on the CPU.
 *
 * ## Why Perlin *and* Worley
 *
 * Neither alone looks like cloud. Perlin noise is smooth and billowy but its features are
 * symmetric blobs with no structure — it reads as fog, or as a lava lamp. Worley (cellular)
 * noise inverted gives exactly the puffy, packed-together cauliflower cells that cumulus is made
 * of, but on its own it is too regular: real cloud is cells inside cells inside cells.
 *
 * The standard answer, from Guerrilla's Nubis work on *Horizon Zero Dawn*, is to remap one by
 * the other: take Perlin's smooth field and use inverted Worley as its lower bound, so the
 * billows keep Perlin's organic variation while acquiring Worley's cellular *shape*. That is the
 * R channel below. The remaining channels are plain Worley at rising frequencies, which the
 * shader combines into an FBM to erode the base shape at successively finer scales — big
 * billows, then smaller ones on their edges, then wisps.
 *
 * ## Everything here is tileable
 *
 * Non-negotiable, because a cloud layer covering the visible world is many kilometres across and
 * the texture is 64 voxels. It gets tiled hundreds of times, and any seam becomes a grid of
 * lines across the whole sky — the single most obvious way procedural clouds give themselves
 * away. Both generators are made periodic the same way: the lattice wraps with a modulo, so the
 * cell at index `n` *is* the cell at index `0`.
 *
 * ## Why the CPU, and why in a worker
 *
 * This runs once, at start-up, and never again. It is ~25 M distance evaluations, which is a
 * few hundred milliseconds of JavaScript — nothing over a session, and far too much to put on
 * the main thread even behind a splash screen (CLAUDE.md: no long blocking work outside the
 * frame path either, and a 400 ms stall is a 400 ms stall). `cloudNoise.worker.ts` runs it and
 * transfers the buffers back, so the boot sequence keeps its frame rate.
 *
 * A compute shader would be faster still. It would also need a WebGPU-only path plus a WebGL2
 * fallback for the same result, and this happens once — so it would be complexity bought with
 * nothing (non-negotiable #5: never ship a feature that breaks the WebGL2 path).
 *
 * `updateFixed` / `updateFrame`: neither. Pure generation, called once.
 */

import { Rng } from '../../core/rng.js';

/** Wrap an integer lattice coordinate into `[0, period)`. Tileability lives here. */
function wrap(value: number, period: number): number {
  const wrapped = value % period;
  return wrapped < 0 ? wrapped + period : wrapped;
}

/**
 * Feature points for a tileable Worley lattice: one point per cell, at a random position in it.
 *
 * Stored as a flat `Float32Array` of xyz triples so the inner loop indexes arithmetic rather
 * than chasing object pointers — this array is read 27 times per voxel per octave, and it is the
 * hot loop of the whole generator.
 */
function worleyPoints(cellsPerAxis: number, random: Rng): Float32Array {
  const points = new Float32Array(cellsPerAxis * cellsPerAxis * cellsPerAxis * 3);
  for (let i = 0; i < points.length; i += 3) {
    points[i] = random.nextFloat();
    points[i + 1] = random.nextFloat();
    points[i + 2] = random.nextFloat();
  }
  return points;
}

/**
 * Inverted, tileable Worley noise at a point.
 *
 * "Inverted" — `1 − distance` — because raw Worley is dark at the cell centres and bright at the
 * boundaries, which is the negative of what cloud looks like. Inverted, each cell becomes a
 * bright rounded lump with dark creases between: a billow.
 *
 * Searches the 3×3×3 neighbourhood, which is sufficient because every feature point is inside
 * its own cell — nothing further away can be nearest.
 */
function worley(
  x: number,
  y: number,
  z: number,
  cellsPerAxis: number,
  points: Float32Array,
): number {
  const cellX = Math.floor(x * cellsPerAxis);
  const cellY = Math.floor(y * cellsPerAxis);
  const cellZ = Math.floor(z * cellsPerAxis);

  let nearest = Number.POSITIVE_INFINITY;

  for (let dz = -1; dz <= 1; dz++) {
    for (let dy = -1; dy <= 1; dy++) {
      for (let dx = -1; dx <= 1; dx++) {
        const wx = wrap(cellX + dx, cellsPerAxis);
        const wy = wrap(cellY + dy, cellsPerAxis);
        const wz = wrap(cellZ + dz, cellsPerAxis);
        const index = ((wz * cellsPerAxis + wy) * cellsPerAxis + wx) * 3;

        // The feature point's position in *unwrapped* space, so the distance is measured to the
        // neighbour we actually looked at rather than to its wrapped-around twin.
        const px = (cellX + dx + points[index]) / cellsPerAxis;
        const py = (cellY + dy + points[index + 1]) / cellsPerAxis;
        const pz = (cellZ + dz + points[index + 2]) / cellsPerAxis;

        const ex = x - px;
        const ey = y - py;
        const ez = z - pz;
        const distanceSquared = ex * ex + ey * ey + ez * ez;
        if (distanceSquared < nearest) nearest = distanceSquared;
      }
    }
  }

  // Normalised so a typical nearest-point distance maps to roughly the full range.
  return Math.max(0, 1 - Math.sqrt(nearest) * cellsPerAxis);
}

/** Worley FBM: three octaves, each half the amplitude and twice the frequency. */
function worleyFbm(
  x: number,
  y: number,
  z: number,
  baseCells: number,
  octaves: readonly Float32Array[],
): number {
  return (
    worley(x, y, z, baseCells, octaves[0]) * 0.625 +
    worley(x, y, z, baseCells * 2, octaves[1]) * 0.25 +
    worley(x, y, z, baseCells * 4, octaves[2]) * 0.125
  );
}

/** Smootherstep — Perlin's improved interpolant, `6t⁵ − 15t⁴ + 10t³`. */
function fade(t: number): number {
  return t * t * t * (t * (t * 6 - 15) + 10);
}

/**
 * Tileable 3D Perlin gradient noise.
 *
 * Twelve gradients pointing at the midpoints of a cube's edges — Perlin's own improved set,
 * which avoids the axis-aligned clustering that a naive random-gradient table produces and which
 * shows up in a cloud field as faint diagonal streaking.
 */
const GRADIENTS = [
  [1, 1, 0], [-1, 1, 0], [1, -1, 0], [-1, -1, 0],
  [1, 0, 1], [-1, 0, 1], [1, 0, -1], [-1, 0, -1],
  [0, 1, 1], [0, -1, 1], [0, 1, -1], [0, -1, -1],
] as const;

function perlin(
  x: number,
  y: number,
  z: number,
  cellsPerAxis: number,
  permutation: Uint8Array,
): number {
  const px = x * cellsPerAxis;
  const py = y * cellsPerAxis;
  const pz = z * cellsPerAxis;

  const x0 = Math.floor(px);
  const y0 = Math.floor(py);
  const z0 = Math.floor(pz);
  const fx = px - x0;
  const fy = py - y0;
  const fz = pz - z0;
  const u = fade(fx);
  const v = fade(fy);
  const w = fade(fz);

  const gradient = (ix: number, iy: number, iz: number, dx: number, dy: number, dz: number): number => {
    const wx = wrap(ix, cellsPerAxis);
    const wy = wrap(iy, cellsPerAxis);
    const wz = wrap(iz, cellsPerAxis);
    const hash = permutation[(permutation[(permutation[wx & 255] + wy) & 255] + wz) & 255];
    const g = GRADIENTS[hash % 12];
    return g[0] * dx + g[1] * dy + g[2] * dz;
  };

  const lerp = (a: number, b: number, t: number): number => a + (b - a) * t;

  const n000 = gradient(x0, y0, z0, fx, fy, fz);
  const n100 = gradient(x0 + 1, y0, z0, fx - 1, fy, fz);
  const n010 = gradient(x0, y0 + 1, z0, fx, fy - 1, fz);
  const n110 = gradient(x0 + 1, y0 + 1, z0, fx - 1, fy - 1, fz);
  const n001 = gradient(x0, y0, z0 + 1, fx, fy, fz - 1);
  const n101 = gradient(x0 + 1, y0, z0 + 1, fx - 1, fy, fz - 1);
  const n011 = gradient(x0, y0 + 1, z0 + 1, fx, fy - 1, fz - 1);
  const n111 = gradient(x0 + 1, y0 + 1, z0 + 1, fx - 1, fy - 1, fz - 1);

  const nx00 = lerp(n000, n100, u);
  const nx10 = lerp(n010, n110, u);
  const nx01 = lerp(n001, n101, u);
  const nx11 = lerp(n011, n111, u);
  const nxy0 = lerp(nx00, nx10, v);
  const nxy1 = lerp(nx01, nx11, v);

  // Perlin's range is roughly ±0.7 in 3D; map to 0…1.
  return lerp(nxy0, nxy1, w) * 0.5 + 0.5;
}

/** Perlin FBM over three octaves. */
function perlinFbm(
  x: number,
  y: number,
  z: number,
  baseCells: number,
  permutation: Uint8Array,
): number {
  return (
    perlin(x, y, z, baseCells, permutation) * 0.5 +
    perlin(x, y, z, baseCells * 2, permutation) * 0.3 +
    perlin(x, y, z, baseCells * 4, permutation) * 0.2
  );
}

/** `remap(v, a, b, c, d)` — the interval-to-interval map the cloud literature is written in. */
export function remap(value: number, low1: number, high1: number, low2: number, high2: number): number {
  return low2 + ((value - low1) * (high2 - low2)) / (high1 - low1);
}

export interface CloudNoiseResult {
  /** RGBA, `size³` voxels. R = Perlin–Worley, GBA = Worley at 1×, 2×, 4×. */
  shape: Uint8Array;
  shapeSize: number;
  /** RGBA (A unused), `detailSize³` voxels. Worley FBM at three rising frequencies. */
  detail: Uint8Array;
  detailSize: number;
}

export interface CloudNoiseOptions {
  /** Edge length of the shape volume. 64 is the shipped value; tests use 8 or 16. */
  shapeSize?: number;
  /** Edge length of the detail volume. */
  detailSize?: number;
  /** Seed. Defaults to the project seed so every session gets the same sky. */
  seed?: string;
}

/**
 * Generate both volumes.
 *
 * Eight bits per channel is deliberate. Density is a quantity the raymarch then *thresholds* and
 * remaps, so what matters is the shape of the field, not its precision; 256 levels is far below
 * anything visible after the coverage remap, and a 16-bit volume would double both the upload
 * and the sampler bandwidth in the inner loop of the most fill-rate-hungry pass in the renderer.
 */
export function generateCloudNoise(options: CloudNoiseOptions = {}): CloudNoiseResult {
  const shapeSize = options.shapeSize ?? 64;
  const detailSize = options.detailSize ?? 32;
  const random = new Rng(options.seed ?? 'skysim-clouds');

  // Permutation table for Perlin, shuffled from the seeded generator.
  const permutation = new Uint8Array(256);
  for (let i = 0; i < 256; i++) permutation[i] = i;
  for (let i = 255; i > 0; i--) {
    const j = random.int(0, i);
    const t = permutation[i];
    permutation[i] = permutation[j];
    permutation[j] = t;
  }

  // --- shape volume -------------------------------------------------------------------------
  // Cell counts are chosen so every octave divides the volume exactly, which is what keeps the
  // result tileable: an octave whose lattice does not fit a whole number of times into the
  // texture wraps at the wrong place and puts a seam in the sky.
  const shapeOctaves = [4, 8, 16].map((cells) => worleyPoints(cells, random));
  const shapeOctavesB = [8, 16, 32].map((cells) => worleyPoints(cells, random));
  const shapeOctavesC = [16, 32, 64].map((cells) => worleyPoints(cells, random));

  const shape = new Uint8Array(shapeSize * shapeSize * shapeSize * 4);
  for (let z = 0; z < shapeSize; z++) {
    for (let y = 0; y < shapeSize; y++) {
      for (let x = 0; x < shapeSize; x++) {
        const u = x / shapeSize;
        const v = y / shapeSize;
        const w = z / shapeSize;

        const worleyLow = worleyFbm(u, v, w, 4, shapeOctaves);
        const perlinValue = perlinFbm(u, v, w, 4, permutation);

        /*
         * The Perlin–Worley remap.
         *
         * `remap(perlin, worley − 1, 1, 0, 1)` uses the inverted-Worley field as a moving floor
         * under the Perlin field. Where Worley is high (a billow centre) the floor rises and the
         * result saturates into a solid lump; where Worley is low (a crease between cells) the
         * floor drops and Perlin's variation shows through as wispy structure. That is what
         * makes the result read as cloud rather than as either ingredient.
         */
        const perlinWorley = clamp01(remap(perlinValue, worleyLow - 1, 1, 0, 1));

        const index = ((z * shapeSize + y) * shapeSize + x) * 4;
        shape[index] = Math.round(perlinWorley * 255);
        shape[index + 1] = Math.round(clamp01(worleyLow) * 255);
        shape[index + 2] = Math.round(clamp01(worleyFbm(u, v, w, 8, shapeOctavesB)) * 255);
        shape[index + 3] = Math.round(clamp01(worleyFbm(u, v, w, 16, shapeOctavesC)) * 255);
      }
    }
  }

  // --- detail volume ------------------------------------------------------------------------
  const detailOctaves = [4, 8, 16].map((cells) => worleyPoints(cells, random));
  const detailOctavesB = [8, 16, 32].map((cells) => worleyPoints(cells, random));
  const detailOctavesC = [16, 32, 64].map((cells) => worleyPoints(cells, random));

  const detail = new Uint8Array(detailSize * detailSize * detailSize * 4);
  for (let z = 0; z < detailSize; z++) {
    for (let y = 0; y < detailSize; y++) {
      for (let x = 0; x < detailSize; x++) {
        const u = x / detailSize;
        const v = y / detailSize;
        const w = z / detailSize;
        const index = ((z * detailSize + y) * detailSize + x) * 4;
        detail[index] = Math.round(clamp01(worleyFbm(u, v, w, 4, detailOctaves)) * 255);
        detail[index + 1] = Math.round(clamp01(worleyFbm(u, v, w, 8, detailOctavesB)) * 255);
        detail[index + 2] = Math.round(clamp01(worleyFbm(u, v, w, 16, detailOctavesC)) * 255);
        detail[index + 3] = 255;
      }
    }
  }

  return { shape, shapeSize, detail, detailSize };
}

function clamp01(value: number): number {
  return value < 0 ? 0 : value > 1 ? 1 : value;
}

/**
 * Trilinear fetch of one channel of an RGBA byte volume, wrapping on every axis.
 *
 * Mirrors what the GPU sampler does to the data {@link generateCloudNoise} produces, so anything
 * evaluated on the CPU — the in-cloud immersion term, the cloud-shadow reference implementation —
 * agrees with what is on screen. Wrapping matters more than it looks: sample coordinates into
 * this field run to several hundred, so every fetch is deep into the tiled repeats.
 *
 * It lives here rather than beside either caller because this module is what *defines* the volume
 * layout, and because it is the one piece of the field two GPU passes and two CPU ports all have
 * to agree on. It imports nothing, which is also what keeps the noise worker free of Three.js.
 */
export function sampleVolume(
  data: Uint8Array,
  size: number,
  channel: number,
  x: number,
  y: number,
  z: number,
): number {
  const fx = (x - Math.floor(x)) * size;
  const fy = (y - Math.floor(y)) * size;
  const fz = (z - Math.floor(z)) * size;
  // Half-texel shift: a GPU sampler addresses texel centres, and skipping this biases every
  // fetch by half a cell, which at 9 km per repeat is 70 m of cloud.
  const x0 = Math.floor(fx - 0.5);
  const y0 = Math.floor(fy - 0.5);
  const z0 = Math.floor(fz - 0.5);
  const tx = fx - 0.5 - x0;
  const ty = fy - 0.5 - y0;
  const tz = fz - 0.5 - z0;

  const at = (ix: number, iy: number, iz: number): number => {
    const wx = ((ix % size) + size) % size;
    const wy = ((iy % size) + size) % size;
    const wz = ((iz % size) + size) % size;
    return data[((wz * size + wy) * size + wx) * 4 + channel] / 255;
  };

  const lerp = (a: number, b: number, t: number): number => a + (b - a) * t;
  const c00 = lerp(at(x0, y0, z0), at(x0 + 1, y0, z0), tx);
  const c10 = lerp(at(x0, y0 + 1, z0), at(x0 + 1, y0 + 1, z0), tx);
  const c01 = lerp(at(x0, y0, z0 + 1), at(x0 + 1, y0, z0 + 1), tx);
  const c11 = lerp(at(x0, y0 + 1, z0 + 1), at(x0 + 1, y0 + 1, z0 + 1), tx);
  return lerp(lerp(c00, c10, ty), lerp(c01, c11, ty), tz);
}

/** Exposed so the tests can check tileability and range directly. */
export { worley, worleyFbm, perlin, perlinFbm, worleyPoints };
